import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { toast } from "react-toastify";
import { createLogger } from "../helpers/logger";
import { queryClient } from "queryClient";
import useNotificationSound from "hooks/useNotificationSound";

type SocketContextType = {
  send: (data: any) => void;
  isConnected: boolean;
};

export const SocketContext = createContext<SocketContextType | null>(null);

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error("useSocket must be used within SocketProvider");
  return ctx;
};

interface SocketProviderProps {
  url: string;
  children: ReactNode;
}

export const SocketProvider = ({ url, children }: SocketProviderProps) => {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { playNotificationSound } = useNotificationSound();

  useEffect(() => {
    const initWS = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // console.log("WebSocket connected");
        // toast.success("WebSocket connected");
        setIsConnected(true);
        ws.send(JSON.stringify({ event: "socket:connect" }));
      };

      ws.onmessage = (event) => {
        const logger = createLogger("WebSocket:onMessage");
        const urlParams = new URLSearchParams(window.location.search);

        const data = JSON.parse(event.data);
        if (data?.event !== "socket:pong") {
          logger.info(`Получили сообщение от сервера: ${JSON.stringify(data)}`);
        }

        // Новое сообщение
        if (data.event === "chat:new_message") {
          const orgID = localStorage.getItem("orgID");
          const newMessage = data.data;
          // 1. Обновляем сообщения чата
          // setQueryData вручную меняет данные в кеше TanStack Query без запроса к серверу и автоматически обновляет UI.
          logger.info(`Добавляем новое сообщение в чат: ${JSON.stringify(newMessage.text)}`);

          if (!newMessage || newMessage.orgID !== orgID) {
            logger.info(`Сообщение не принадлежит текущей организации: ${JSON.stringify(newMessage)}`);
            return;
          }

          queryClient.setQueryData(["chat", newMessage.chatID], (oldMessages: any) => {
            if (oldMessages) {
              return [...oldMessages, newMessage];
            }
            return [newMessage];
          });

          // Добавляем звуковое уведомление
          // playNotificationSound("У вас новое сообщение");
          // toast.success("У вас новое сообщение");

          // 2️⃣ обновляем список чатов
          logger.info(
            `Обновляем сообщение в чате: ${JSON.stringify({
              orgID,
              key: ["chats", orgID],
            })}`
          );
          queryClient.setQueryData(["chats", orgID], (old: any[]) => {
            console.log("newMessage.old", old);
            if (old) {
              return (
                old
                  .map((chat: any) =>
                    chat.id === newMessage.chatID
                      ? {
                        ...chat,
                        lastMessage: newMessage.text,
                        unreadCount: chat.unreadCount + 1,
                        updatedAt: newMessage.createdAt,
                      }
                      : chat
                  )
                  // поднимаем чат наверх
                  .sort((a: any, b: any) => b.updatedAt.localeCompare(a.updatedAt))
              );
            }
            return [];
          });
        } else if (data.event === "chat:new") {
          const newChat = data.data;
          const orgID = localStorage.getItem("orgID");

          logger.info(
            `Добалвяем новый чат в список чатов: ${JSON.stringify({
              orgID,
              key: ["chats", orgID],
            })}`
          );

          queryClient.setQueriesData({
            queryKey: ["chats", orgID, newChat.source, newChat.externalAccountUserName],
          }, (old: any[]) => {
            if (old) {
              return [newChat, ...old];
            }
            return [];
          });
        }
        // // Ошибка
        // else if (data.event === "chat:error") {
        //   logger.error(`Ошибка при получении сообщения: ${JSON.stringify(data.data)}`);
        //   toast.error(data.data.message);
        // }
        // // Обновление чата
        // else if (data.event === "chat:updated" && data.data.chatID == chatID) {
        //   setChatUpdates(data.data);
        // }
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setIsConnected(false);
      };

      ws.onerror = (err) => {
        console.error("WebSocket error", err);
        setIsConnected(false);
      };
    };

    initWS();

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ event: "socket:ping" }));
      } else if (
        wsRef.current?.readyState === WebSocket.CLOSING ||
        wsRef.current?.readyState === WebSocket.CLOSED
      ) {
        initWS();
        // toast.info("Reconnecting WebSocket...");
      }
    }, 10000);

    return () => {
      wsRef.current?.close();
      clearInterval(pingInterval);
    };
  }, [url]);

  const send = (data: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      console.warn("WebSocket not open");
    }
  };

  return <SocketContext.Provider value={{ send, isConnected }}>{children}</SocketContext.Provider>;
};
